import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ActivationKeysService,
  CreateStandaloneActivationKeyRequest,
  StandaloneActivationKey,
} from 'src/app/services/activation-keys.service';
import { ErrorService } from 'src/app/services/error.service';
import { Product, ProductsService } from 'src/app/services/products.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-tenant-activation-keys',
  templateUrl: './tenant-activation-keys.component.html',
  styleUrl: './tenant-activation-keys.component.scss',
})
export class TenantActivationKeysComponent implements OnChanges {
  @Input() tenantId?: string;

  @ViewChild('createDialog')
  createDialog!: ElementRef<HTMLDialogElement>;

  items: StandaloneActivationKey[] = [];
  loading = false;
  error = '';
  collapsed = false;

  shownKeys = new Set<string>();
  hiddenKeyString = [9, 10, 10, 3, 7]
    .map((length) => '&bull;'.repeat(length))
    .join('-');

  productSource: 'catalog' | 'custom' = 'catalog';
  products: Product[] = [];
  loadingProducts = false;
  showInactive = false;
  productSearch = '';
  selectedProduct: Product | null = null;
  customProductCode = '';
  customProductTitle = '';
  uniqueId = '';
  expirationDate = '';
  creating = false;
  createError = '';
  createdKey: StandaloneActivationKey | null = null;
  copied = false;

  constructor(
    private activationKeysService: ActivationKeysService,
    private productsService: ProductsService,
    private errorService: ErrorService,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenantId'] && this.tenantId) {
      this.load();
    }
  }

  load() {
    if (!this.tenantId) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.activationKeysService
      .listStandaloneActivationKeys(this.tenantId)
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.items = res.standalone_activation_keys ?? [];
        },
        error: (err) => {
          this.loading = false;
          this.error = this.errorService.getErrorMessage(err);
        },
      });
  }

  get filteredProducts(): Product[] {
    const q = this.productSearch.trim().toLowerCase();
    const list = this.products;
    if (!q) {
      return list.filter((p) => this.showInactive || p.active);
    }
    return list.filter((p) => {
      return (
        (this.showInactive || p.active) &&
        (p.code?.toLowerCase().includes(q) ||
          p.title?.toLowerCase().includes(q) ||
          p.variance_code?.toLowerCase().includes(q))
      );
    });
  }

  get canSubmit(): boolean {
    const hasKeyFields =
      this.uniqueId.trim().length > 0 &&
      this.expirationDate.trim().length > 0 &&
      !this.creating;
    if (!hasKeyFields) {
      return false;
    }
    if (this.productSource === 'custom') {
      return (
        /^\d{3}$/.test(this.customProductCode.trim()) &&
        this.customProductTitle.trim().length > 0
      );
    }
    return /^\d{3}$/.test(this.selectedProduct?.code?.trim() ?? '');
  }

  onShowInactiveChange() {
    if (
      !this.showInactive &&
      this.selectedProduct &&
      !this.selectedProduct.active
    ) {
      this.selectedProduct = null;
    }
  }

  showCreateDialog() {
    this.resetCreateForm();
    this.loadProducts();
    this.createDialog.nativeElement.showModal();
  }

  closeCreateDialog() {
    this.createDialog.nativeElement.close();
    this.resetCreateForm();
  }

  selectProduct(product: Product) {
    this.selectedProduct = product;
  }

  create() {
    if (!this.tenantId || !this.canSubmit) {
      return;
    }

    this.creating = true;
    this.createError = '';
    const body: CreateStandaloneActivationKeyRequest = {
      tenant_id: this.tenantId,
      unique_id: this.uniqueId.trim(),
      expiration_date: this.expirationDate.trim(),
    };
    if (this.productSource === 'catalog' && this.selectedProduct) {
      body.product_id = this.selectedProduct.id;
    } else {
      body.product_code = this.customProductCode.trim();
      body.product_title = this.customProductTitle.trim();
    }

    this.activationKeysService
      .createStandaloneActivationKey(body)
      .subscribe({
        next: (res) => {
          this.creating = false;
          this.createdKey = res.standalone_activation_key;
          this.load();
          if (this.createdKey) {
            this.shownKeys.add(this.createdKey.id);
          }
        },
        error: (err) => {
          this.creating = false;
          this.createError = this.errorService.getErrorMessage(err);
        },
      });
  }

  copyCreatedKey() {
    const key = this.createdKey?.activation_key;
    if (!key) {
      return;
    }
    navigator.clipboard.writeText(key).then(() => {
      this.copied = true;
    });
  }

  copyKey(item: StandaloneActivationKey, event?: Event) {
    event?.stopPropagation();
    if (!item.activation_key) {
      return;
    }
    navigator.clipboard.writeText(item.activation_key).then(() => {
      this.toastService.addToast('info', 'Activation key copied to clipboard');
    });
  }

  toggleKey(id: string) {
    if (this.shownKeys.has(id)) {
      this.shownKeys.delete(id);
    } else {
      this.shownKeys.add(id);
    }
  }

  private loadProducts() {
    this.loadingProducts = true;
    this.productsService.getAllProducts().subscribe({
      next: (res) => {
        this.products = (res.products ?? [])
          .slice()
          .sort((a, b) => a.code.localeCompare(b.code));
        this.loadingProducts = false;
      },
      error: (err) => {
        this.loadingProducts = false;
        this.createError = this.errorService.getErrorMessage(err);
      },
    });
  }

  private resetCreateForm() {
    this.productSource = 'catalog';
    this.productSearch = '';
    this.selectedProduct = null;
    this.customProductCode = '';
    this.customProductTitle = '';
    this.uniqueId = '';
    this.expirationDate = '';
    this.creating = false;
    this.createError = '';
    this.createdKey = null;
    this.copied = false;
  }
}
